import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	OverviewBreak,
	OverviewCleaningStats,
	OverviewData,
	OverviewHeader,
	OverviewLastBar,
	OverviewLiveSignals,
	OverviewProductionRunMetrics,
	OverviewProductRun,
	OverviewShiftMetrics,
} from "../models/types";

const moment = getMomentTz();

// SQL Server returns datetimes without timezone suffix → parse as UTC
// to avoid the browser interpreting them as local time.
const parseUtc = (s: string | null | undefined): Date | null =>
	s ? moment.utc(s).toDate() : null;

type RawHeader = Omit<
	OverviewHeader,
	| "ServerTime"
	| "CurrShiftStart"
	| "CurrShiftEnd"
	| "PrevShiftStart"
	| "PrevShiftEnd"
> & {
	ServerTime: string;
	CurrShiftStart: string | null;
	CurrShiftEnd: string | null;
	PrevShiftStart: string | null;
	PrevShiftEnd: string | null;
};

type RawShiftMetrics = Omit<OverviewShiftMetrics, "ShiftStart" | "ShiftEnd"> & {
	ShiftStart: string | null;
	ShiftEnd: string | null;
};

type RawProductionRunMetrics = Omit<
	OverviewProductionRunMetrics,
	"ShiftStart" | "ShiftEnd" | "RunStart" | "RunEnd"
> & {
	ShiftStart: string | null;
	ShiftEnd: string | null;
	RunStart: string | null;
	RunEnd: string | null;
};

type RawBar = Omit<OverviewLastBar, "Timestamp"> & { Timestamp: string };

type RawProductRun = Omit<OverviewProductRun, "StartedAt" | "CompletedAt"> & {
	StartedAt: string | null;
	CompletedAt: string | null;
};

const mapHeader = (r: RawHeader): OverviewHeader => ({
	AssetId: r.AssetId,
	AssetName: r.AssetName,
	ServerTime: moment.utc(r.ServerTime).toDate(),
	CurrShiftId: r.CurrShiftId,
	CurrShiftStart: parseUtc(r.CurrShiftStart),
	CurrShiftEnd: parseUtc(r.CurrShiftEnd),
	CurrShiftLabel: r.CurrShiftLabel,
	CurrCrew: r.CurrCrew,
	PrevShiftId: r.PrevShiftId,
	PrevShiftStart: parseUtc(r.PrevShiftStart),
	PrevShiftEnd: parseUtc(r.PrevShiftEnd),
	PrevShiftLabel: r.PrevShiftLabel,
	PrevCrew: r.PrevCrew,
});

const mapShiftMetrics = (r: RawShiftMetrics): OverviewShiftMetrics => ({
	...r,
	ShiftStart: parseUtc(r.ShiftStart),
	ShiftEnd: parseUtc(r.ShiftEnd),
});

const mapProductionRunMetrics = (
	r: RawProductionRunMetrics,
): OverviewProductionRunMetrics => ({
	...r,
	ShiftStart: parseUtc(r.ShiftStart),
	ShiftEnd: parseUtc(r.ShiftEnd),
	RunStart: parseUtc(r.RunStart),
	RunEnd: parseUtc(r.RunEnd),
});

const mapLastBar = (r: RawBar): OverviewLastBar => ({
	...r,
	Timestamp: moment.utc(r.Timestamp).toDate(),
});

const mapProductRun = (r: RawProductRun): OverviewProductRun => ({
	...r,
	StartedAt: parseUtc(r.StartedAt),
	CompletedAt: parseUtc(r.CompletedAt),
});

export type RawBreak = {
	EventId: number;
	BreakLabel: string;
	Start: string;
	End: string;
	IsSpell: boolean | number | string;
	RanAfter: boolean | number | string;
};

// BIT can arrive as a string depending on the serializer
const toBit = (v: boolean | number | string): boolean =>
	v === true || v === 1 || v === "1" || v === "true";

export const mapBreak = (r: RawBreak): OverviewBreak => ({
	EventId: r.EventId,
	BreakLabel: r.BreakLabel,
	Start: moment.utc(r.Start).toDate(),
	End: moment.utc(r.End).toDate(),
	IsSpell: toBit(r.IsSpell),
	RanAfter: toBit(r.RanAfter),
});

export const getWeldlineOverview = async (
	assetId: number | null = null,
): Promise<OverviewData> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "AssetId", value: assetId }];

	const resp = await apiService.callV2(
		"[FAB].[GetWeldlineOverview]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching weldline overview");
	}

	const headerRows = get(resp, "data.tables[0].rows", []) as RawHeader[];
	const liveRows = get(
		resp,
		"data.tables[1].rows",
		[],
	) as OverviewLiveSignals[];
	const currRows = get(resp, "data.tables[2].rows", []) as RawShiftMetrics[];
	const prevRows = get(resp, "data.tables[3].rows", []) as RawShiftMetrics[];
	const barRows = get(resp, "data.tables[4].rows", []) as RawBar[];
	const cleaningRows = get(
		resp,
		"data.tables[5].rows",
		[],
	) as OverviewCleaningStats[];
	const currentRunRows = get(
		resp,
		"data.tables[6].rows",
		[],
	) as RawProductionRunMetrics[];
	const productRunRows = get(
		resp,
		"data.tables[7].rows",
		[],
	) as RawProductRun[];
	const breakRows = get(resp, "data.tables[8].rows", []) as RawBreak[];

	return {
		header: headerRows[0] ? mapHeader(headerRows[0]) : null,
		live: liveRows[0] ?? null,
		currentShift: currRows[0] ? mapShiftMetrics(currRows[0]) : null,
		previousShift: prevRows[0] ? mapShiftMetrics(prevRows[0]) : null,
		currentRun: currentRunRows[0]
			? mapProductionRunMetrics(currentRunRows[0])
			: null,
		productRuns: productRunRows.map(mapProductRun),
		lastBars: barRows.map(mapLastBar),
		cleaning: cleaningRows,
		breaks: breakRows.map(mapBreak),
	};
};
